import { CategoryDivider } from '@affine/core/modules/app-sidebar/views';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { SIDEBAR_ATOM_ROW_HEIGHT } from '../../../../modules/app-sidebar/views/sidebar-layout';
import { AnimatedNavigationPanelTreeDisclosureContent } from '../tree/disclosure-content';
import { content, header, root } from './collapsible-section.css';

interface CollapsibleSectionProps extends PropsWithChildren {
  path: string[];
  title: ReactNode;
  titleInteractive?: boolean;
  actions?: ReactNode;

  className?: string;
  testId?: string;
  rootDataAttributes?: Record<`data-${string}`, string | undefined>;

  headerRef?: RefObject<HTMLDivElement>;
  headerTestId?: string;
  headerClassName?: string;

  contentClassName?: string;
  contentStyle?: CSSProperties;
  revealOnExpand?: boolean;
  revealOnMount?: boolean;
}

const SECTION_EXPAND_REVEAL_DELAY_MS = 190;
const MIN_REVEAL_SCROLL_DELTA = SIDEBAR_ATOM_ROW_HEIGHT / 2;

function findSidebarScrollViewport(element: HTMLElement) {
  return element.closest<HTMLElement>('[data-sidebar-scroll-viewport="true"]');
}

function scrollExpandedSectionIntoView(root: HTMLElement) {
  const viewport = findSidebarScrollViewport(root);
  const header = root.querySelector<HTMLElement>(
    '[data-navigation-panel-section-header="true"]'
  );
  if (!viewport || !header) return;

  const viewportRect = viewport.getBoundingClientRect();
  const headerRect = header.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  const maxScrollTop = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight
  );

  const scrollToRevealTarget = (targetScrollTop: number) => {
    const clampedTargetScrollTop = Math.min(
      maxScrollTop,
      Math.max(0, targetScrollTop)
    );
    const rawDelta = clampedTargetScrollTop - viewport.scrollTop;
    if (Math.abs(rawDelta) < MIN_REVEAL_SCROLL_DELTA) return;

    const atomDelta =
      Math.sign(rawDelta) *
      Math.round(Math.abs(rawDelta) / SIDEBAR_ATOM_ROW_HEIGHT) *
      SIDEBAR_ATOM_ROW_HEIGHT;
    if (atomDelta === 0) return;

    const nextScrollTop = Math.min(
      maxScrollTop,
      Math.max(0, viewport.scrollTop + atomDelta)
    );
    if (
      Math.abs(nextScrollTop - viewport.scrollTop) < MIN_REVEAL_SCROLL_DELTA
    ) {
      return;
    }

    viewport.scrollTo({
      top: nextScrollTop,
      behavior: 'smooth',
    });
  };

  if (headerRect.top < viewportRect.top) {
    scrollToRevealTarget(
      viewport.scrollTop + headerRect.top - viewportRect.top
    );
    return;
  }

  if (headerRect.bottom > viewportRect.bottom) {
    scrollToRevealTarget(
      viewport.scrollTop + headerRect.bottom - viewportRect.bottom
    );
    return;
  }

  const overflowBottom = rootRect.bottom - viewportRect.bottom;
  if (overflowBottom <= 0) return;

  const headerTopRoom = Math.max(0, headerRect.top - viewportRect.top);
  const scrollDelta = Math.min(overflowBottom, headerTopRoom);
  if (scrollDelta <= 0) return;

  scrollToRevealTarget(viewport.scrollTop + scrollDelta);
}

function scrollCreatedSectionToTop(root: HTMLElement) {
  const viewport = findSidebarScrollViewport(root);
  const header = root.querySelector<HTMLElement>(
    '[data-navigation-panel-section-header="true"]'
  );
  if (!viewport || !header) return;

  const viewportRect = viewport.getBoundingClientRect();
  const headerRect = header.getBoundingClientRect();
  const maxScrollTop = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight
  );
  viewport.scrollTo({
    top: Math.min(
      maxScrollTop,
      Math.max(0, viewport.scrollTop + headerRect.top - viewportRect.top)
    ),
    behavior: 'smooth',
  });
}

export const CollapsibleSection = ({
  path,
  title,
  titleInteractive = false,
  actions,
  children,

  className,
  testId,
  rootDataAttributes,

  headerRef,
  headerTestId,
  headerClassName,

  contentClassName,
  contentStyle,
  revealOnExpand = false,
  revealOnMount = false,
}: CollapsibleSectionProps) => {
  const navigationPanelService = useService(NavigationPanelService);

  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previousCollapsedRef = useRef(collapsed);

  const setCollapsed = useCallback(
    (v: boolean) => {
      navigationPanelService.setCollapsed(path, v);
    },
    [navigationPanelService, path]
  );

  useEffect(() => {
    const wasCollapsed = previousCollapsedRef.current;
    previousCollapsedRef.current = collapsed;
    if (!revealOnExpand || collapsed || wasCollapsed !== true) return;

    const root = rootRef.current;
    if (!root) return;

    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        scrollExpandedSectionIntoView(root);
      });
    });
    const timeout = window.setTimeout(() => {
      scrollExpandedSectionIntoView(root);
    }, SECTION_EXPAND_REVEAL_DELAY_MS);
    return () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) cancelAnimationFrame(secondFrame);
      window.clearTimeout(timeout);
    };
  }, [collapsed, revealOnExpand]);

  useEffect(() => {
    if (!revealOnMount) return;
    const root = rootRef.current;
    if (!root) return;

    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        scrollCreatedSectionToTop(root);
      });
    });
    const timeout = window.setTimeout(() => {
      scrollCreatedSectionToTop(root);
    }, SECTION_EXPAND_REVEAL_DELAY_MS);
    return () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) cancelAnimationFrame(secondFrame);
      window.clearTimeout(timeout);
    };
  }, [revealOnMount]);

  return (
    <Collapsible.Root
      {...rootDataAttributes}
      data-collapsed={collapsed}
      className={clsx(root, className)}
      open={!collapsed}
      ref={rootRef}
      data-testid={testId}
    >
      <CategoryDivider
        data-testid={headerTestId}
        data-navigation-panel-section-header="true"
        label={title}
        interactiveLabel={titleInteractive}
        setCollapsed={setCollapsed}
        collapsed={collapsed}
        ref={headerRef}
        className={clsx(header, headerClassName)}
      >
        {actions}
      </CategoryDivider>
      <AnimatedNavigationPanelTreeDisclosureContent
        collapsed={collapsed}
        data-testid="collapsible-section-content"
        className={clsx(content, contentClassName)}
        style={contentStyle}
      >
        {children}
      </AnimatedNavigationPanelTreeDisclosureContent>
    </Collapsible.Root>
  );
};
